import React from 'react';
import { CheckCircle2, Award, Shield, Users } from 'lucide-react';
import { businessInfo } from '../mock';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality Craftsmanship',
      description: 'Every project is completed with meticulous attention to detail and the highest standards of workmanship.'
    },
    {
      icon: Shield,
      title: 'Licensed & Insured',
      description: 'Fully licensed (PA #159448) and insured for your complete peace of mind and protection.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We communicate clearly and work to exceed your expectations.'
    },
    {
      icon: CheckCircle2,
      title: 'Reliable Service',
      description: 'On-time, on-budget, and always professional. We respect your home and your time.'
    }
  ];

  return (
    <div className="about-page">
      <SEO 
        title="About SBF Home Improvements | Licensed Contractor Mt Pleasant, PA 15666"
        description="Learn about SBF Home Improvements LLC. 15+ years serving Mt Pleasant (15666) & 20-mile radius with quality roofing, siding, flooring & home repairs. PA License #159448."
        keywords="about SBF, home improvement company 15666, licensed contractor Mt Pleasant, family-owned business PA"
      />
      {/* Hero Section */}
      <section style={{background: 'var(--bg-page)', padding: '120px 40px 80px'}}>
        <div className="container">
          <h1 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>ABOUT US</h1>
          <p className="body-large" style={{color: 'var(--text-secondary)', maxWidth: '60ch'}}>Professional home improvement services you can trust</p>
        </div>
      </section>

      {/* Story Section */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center'}}>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_home-fix-experts-2/artifacts/qxhq7h3r_20250429_145050.jpg"
                alt="SBF Home Improvements - Carl on the job"
                style={{width: '100%', height: 'auto', borderRadius: '8px'}}
              />
            </div>
            <div>
              <h2 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>YOUR TRUSTED HOME IMPROVEMENT PARTNER</h2>
              <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                <p className="body-medium" style={{color: 'var(--text-secondary)', lineHeight: '1.6'}}>
                  At SBF Home Improvements LLC, we've been transforming houses into dream homes for over 15 years. Based in Mt Pleasant, PA (15666) and serving a 20-mile radius including Scottdale, Connellsville, and Uniontown, we specialize in roofing, siding, flooring, and general home repairs.
                </p>
                <p className="body-medium" style={{color: 'var(--text-secondary)', lineHeight: '1.6'}}>
                  Our team of skilled craftsmen takes pride in delivering exceptional quality on every project, from minor repairs to complete home renovations. We believe in honest communication, fair pricing, and work that stands the test of time.
                </p>
                <p className="body-medium" style={{color: 'var(--text-secondary)', lineHeight: '1.6'}}>
                  As a fully licensed and insured company, we handle every project with professionalism and respect for your property. Your satisfaction isn't just our goal—it's our guarantee.
                </p>
              </div>
              <div style={{marginTop: '32px', padding: '24px', background: 'var(--bg-page)', borderRadius: '8px', borderLeft: '4px solid var(--brand-primary)'}}>
                <p className="heading-5" style={{color: 'var(--text-primary)', marginBottom: '8px'}}>PA License #{businessInfo.license}</p>
                <p className="body-small" style={{color: 'var(--text-secondary)'}}>Fully Licensed & Insured</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section style={{background: 'var(--bg-page)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>WHY CHOOSE US</h2>
            <p className="body-medium" style={{color: 'var(--text-secondary)', maxWidth: '50ch', margin: '0 auto'}}>Built on integrity, craftsmanship, and customer satisfaction</p>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px'}}>
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} style={{textAlign: 'center'}}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}>
                    <IconComponent size={40} color="var(--text-inverse)" />
                  </div>
                  <h3 className="heading-5" style={{color: 'var(--text-primary)', marginBottom: '12px'}}>{value.title}</h3>
                  <p className="body-small" style={{color: 'var(--text-secondary)', lineHeight: '1.6'}}>{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <h2 className="heading-2" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>SERVICE AREAS</h2>
            <p className="body-medium" style={{color: 'var(--text-secondary)'}}>Proudly serving Mt Pleasant (15666) and 20-mile radius</p>
          </div>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center'}}>
            <div style={{
              padding: '16px 32px',
              background: 'var(--bg-page)',
              borderRadius: '100px',
              border: '1px solid var(--border-medium)'
            }}>
              <span className="body-medium" style={{color: 'var(--text-primary)'}}>Fayette County</span>
            </div>
            <div style={{
              padding: '16px 32px',
              background: 'var(--bg-page)',
              borderRadius: '100px',
              border: '1px solid var(--border-medium)'
            }}>
              <span className="body-medium" style={{color: 'var(--text-primary)'}}>Westmoreland County</span>
            </div>
            <div style={{
              padding: '16px 32px',
              background: 'var(--bg-page)',
              borderRadius: '100px',
              border: '1px solid var(--border-medium)'
            }}>
              <span className="body-medium" style={{color: 'var(--text-primary)'}}>Allegheny County</span>
            </div>
            <div style={{
              padding: '16px 32px',
              background: 'var(--bg-page)',
              borderRadius: '100px',
              border: '1px solid var(--border-medium)'
            }}>
              <span className="body-medium" style={{color: 'var(--text-primary)'}}>Surrounding Areas</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;